import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Download, Mail, Plus, Trash2, FileText } from 'lucide-react';
import { logger } from '../../utils/logger';
import { SignaturePad } from '../../components/admin/SignaturePad';
import { EmailRecipients } from '../../components/admin/EmailRecipients';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_OTHER_VAC = { vaccination_date: '', vaccination_type: '', vaccine_make_serial: '', valid_until: '' };
const EMPTY_RABIES = {
  vaccination_date: '',
  vaccine_make_serial: '',
  validity_from: '',
  validity_to: '',
  serology_sampling_date: '',
  serology_lab: '',
  antibody_titration_result: '',
  serology_validity_from: '',
  serology_validity_to: '',
};

const initial = {
  certificate_number: '',
  consignor_name: '',
  consignor_address: '',
  consignor_telephone: '',
  consignee_name: '',
  consignee_address: '',
  consignee_telephone: '',
  species: '',
  breed: '',
  animal_name: '',
  age_or_birthdate: '',
  colour_markings: '',
  sex_and_status: '',
  microchip_number: '',
  microchip_location: '',
  microchip_implant_date: '',
  rabies: {
    vaccination_date: '',
    vaccine_make_serial: '',
    validity_from: '',
    validity_to: '',
    serology_sampling_date: '',
    serology_lab: '',
    antibody_titration_result: '',
    serology_validity_from: '',
    serology_validity_to: '',
  },
  rabies_entries: [{ ...EMPTY_RABIES }],
  other_vaccines: [],
  acaricidal_preparation: '',
  acaricidal_given: null,
  tapeworm_preparation: '',
  tapeworm_given: null,
  export_date: '',
  returning_date: '',
  ci_passport_number: '',
  veterinarian_name: 'Dr. Samantha Dorman',
  veterinarian_license: '#139',
  signature_image: null,
  signed_at: null,
  signed_by: null,
  certificate_issue_date: '',
  status: 'draft',
  notes: '',
};

const Field = ({ label, children, full = false }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="block text-xs font-semibold text-foreground/70 mb-1">{label}</label>
    {children}
  </div>
);

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className={`w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary ${props.className || ''}`}
  />
);

const TextArea = (props) => (
  <textarea
    rows={3}
    {...props}
    className={`w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-y ${props.className || ''}`}
  />
);

export const HealthCertificate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  const password = sessionStorage.getItem('admin_password');

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [error, setError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipients: '', cc: '', subject: '', message: '' });
  const [emailReview, setEmailReview] = useState(null);
  const emailLock = useRef(false);
  useEffect(() => { setEmailReview(null); }, [emailForm]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState('');
  const [defaultSignature, setDefaultSignature] = useState(null);

  const headers = useMemo(() => ({
    Authorization: `Basic ${btoa(':' + password)}`,
    'Content-Type': 'application/json',
  }), [password]);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    if (location.search.includes('email=1')) setEmailOpen(true);
    // Load the saved default signature so the vet doesn't have to re-draw it
    fetch(`${API_URL}/api/admin/health-certificates/settings/signature`, { headers })
      .then((r) => r.json())
      .then((d) => setDefaultSignature(d.signature_image || null))
      .catch(() => {});

    // Prefill from an existing relocation record when ?relocation_id= is set
    const params = new URLSearchParams(location.search);
    const relocationId = params.get('relocation_id');
    if (!isEdit && relocationId) {
      fetch(`${API_URL}/api/admin/relocations/${relocationId}/health-cert-prefill`, { headers })
        .then((r) => r.ok ? r.json() : Promise.reject())
        .then((p) => {
          setForm((f) => ({
            ...f,
            certificate_issue_date: p.certificate_issue_date || f.certificate_issue_date,
            export_date: p.export_date || f.export_date,
            destination_country: p.destination_country || f.destination_country,
            owner_name: p.owner_name || f.owner_name,
            owner_address: p.owner_address || f.owner_address,
            owner_phone: p.owner_phone || f.owner_phone,
            animal_name: p.animal_name || f.animal_name,
            species: p.species || f.species,
            breed: p.breed || f.breed,
            weight: p.weight || f.weight,
            microchip_number: p.microchip_number || f.microchip_number,
            relocation_id: relocationId,
            pet_id: p.pet_id || null,
            rabies_entries: p.rabies_last_date
              ? [{ ...EMPTY_RABIES, ...(f.rabies_entries?.[0] || {}), date_of_vaccination: p.rabies_last_date }]
              : f.rabies_entries,
            rabies_titer_test_date: p.rabies_titer_test_date || f.rabies_titer_test_date,
          }));
        })
        .catch(() => {});
    }

    if (isEdit) {
      fetch(`${API_URL}/api/admin/health-certificates/${id}`, { headers })
        .then((r) => r.json())
        .then((d) => {
          // Backward compat: if rabies_entries missing/empty, seed from legacy
          // single rabies object so older records keep their data on screen.
          let entries = Array.isArray(d.rabies_entries) ? d.rabies_entries : [];
          entries = entries.filter(Boolean);
          if (entries.length === 0) {
            entries = [{ ...EMPTY_RABIES, ...(d.rabies || {}) }];
          } else {
            entries = entries.map((e) => ({ ...EMPTY_RABIES, ...(e || {}) }));
          }
          setForm({
            ...initial,
            ...d,
            rabies: { ...initial.rabies, ...(d.rabies || {}) },
            rabies_entries: entries,
          });
          setSavedId(d.id);
        })
        .catch((e) => logger.error('Load HC failed', e));
    }
  }, [id, isEdit, headers, navigate, password, location.search]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRabies = (k, v) => setForm((f) => ({ ...f, rabies: { ...f.rabies, [k]: v } }));
  const setRabiesEntry = (idx, k, v) => setForm((f) => {
    const arr = [...(f.rabies_entries || [])];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...f, rabies_entries: arr };
  });
  const addRabiesEntry = () => setForm((f) => ({ ...f, rabies_entries: [...(f.rabies_entries || []), { ...EMPTY_RABIES }] }));
  const removeRabiesEntry = (idx) => setForm((f) => {
    const next = (f.rabies_entries || []).filter((_, i) => i !== idx);
    return { ...f, rabies_entries: next.length === 0 ? [{ ...EMPTY_RABIES }] : next };
  });
  const setOtherVac = (idx, k, v) => setForm((f) => {
    const arr = [...f.other_vaccines];
    arr[idx] = { ...arr[idx], [k]: v };
    return { ...f, other_vaccines: arr };
  });
  const addOtherVac = () => setForm((f) => ({ ...f, other_vaccines: [...f.other_vaccines, { ...EMPTY_OTHER_VAC }] }));
  const removeOtherVac = (idx) => setForm((f) => ({ ...f, other_vaccines: f.other_vaccines.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { ...form };
      const url = savedId
        ? `${API_URL}/api/admin/health-certificates/${savedId}`
        : `${API_URL}/api/admin/health-certificates`;
      const method = savedId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      const data = await res.json();
      setSavedId(data.id);
      let entries = Array.isArray(data.rabies_entries) ? data.rabies_entries : [];
      entries = entries.filter(Boolean).map((e) => ({ ...EMPTY_RABIES, ...(e || {}) }));
      if (entries.length === 0) entries = [{ ...EMPTY_RABIES, ...(data.rabies || {}) }];
      setForm({
        ...initial,
        ...data,
        rabies: { ...initial.rabies, ...(data.rabies || {}) },
        rabies_entries: entries,
      });
      if (!savedId) navigate(`/admin/health-certificates/${data.id}/edit`, { replace: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!savedId) { setError('Please save the certificate first.'); return; }
    const res = await fetch(`${API_URL}/api/admin/health-certificates/${savedId}/pdf`, { headers });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `HealthCert_${form.animal_name || 'Pet'}_${form.certificate_number}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const saveDefaultSig = async (dataUrl) => {
    try {
      await fetch(`${API_URL}/api/admin/health-certificates/settings/signature`, {
        method: 'PUT', headers, body: JSON.stringify({ signature_image: dataUrl }),
      });
      setDefaultSignature(dataUrl);
    } catch (e) { logger.error('Save default sig failed', e); }
  };

  const useDefaultSig = () => setField('signature_image', defaultSignature);

  const clearDefaultSig = async () => {
    if (!window.confirm('Remove the saved default signature?')) return;
    try {
      await fetch(`${API_URL}/api/admin/health-certificates/settings/signature`, {
        method: 'DELETE', headers,
      });
      setDefaultSignature(null);
    } catch (e) { logger.error('Clear default sig failed', e); }
  };

  const sendEmail = async () => {
    if (emailLock.current) return;
    if (!savedId) { setError('Please save the certificate first.'); return; }
    setEmailResult('');
    const recipients = emailForm.recipients.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    const cc = emailForm.cc.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { setEmailResult('Please add at least one recipient.'); return; }
    emailLock.current = true;
    setEmailSending(true);
    try {
      if (!emailReview) {
        const res = await fetch(`${API_URL}/api/admin/emails/recipients/preview`, { method: 'POST', headers, body: JSON.stringify({ to: recipients, cc }) });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check the recipients.');
        setEmailReview(data);
        return;
      }
      const res = await fetch(`${API_URL}/api/admin/health-certificates/${savedId}/email`, {
        method: 'POST', headers,
        body: JSON.stringify({ recipients: emailReview.to_recipients, cc: emailReview.cc, subject: emailForm.subject || null, message: emailForm.message || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Email failed');
      const failed = (data.failed || []).map((f) => `${f.recipient}: ${f.error}`).join('; ');
      setEmailResult(failed
        ? `Sent to ${data.sent.length}. Failed: ${failed}. Check Email History before resending.`
        : `Sent to ${data.sent.join(', ')} ✓`);
      if (!failed) {
        setEmailForm({ recipients: '', cc: '', subject: '', message: '' });
      }
    } catch (e) {
      setEmailResult(`Failed: ${e.message}`);
    } finally {
      setEmailSending(false);
      emailLock.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {isEdit ? 'Edit Health Certificate' : 'New Health Certificate'}
              </h1>
              <p className="text-sm text-foreground/60">
                {form.certificate_number ? <>Certificate <span className="font-mono">{form.certificate_number}</span></> : 'Saving will assign HC-YYYY-NNN'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate('/admin/health-certificates')} data-testid="back-btn"
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> List
            </button>
            <button onClick={save} disabled={saving} data-testid="save-btn"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button onClick={downloadPdf} disabled={!savedId} data-testid="pdf-btn"
              className="px-4 py-2 text-sm border-2 border-primary text-primary rounded-lg hover:bg-primary/10 disabled:opacity-40 flex items-center gap-2">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={() => setEmailOpen((v) => !v)} disabled={!savedId} data-testid="email-btn"
              className="px-4 py-2 text-sm border-2 border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50 disabled:opacity-40 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>
        </div>
        {error && <div className="bg-red-50 border-t border-red-200 px-6 py-2 text-sm text-red-800">{error}</div>}
      </div>

      <div className="container mx-auto px-6 py-6 space-y-5">
        {/* Email panel */}
        {emailOpen && savedId && (
          <div className="bg-white rounded-xl border-2 border-emerald-600 shadow-sm p-5" data-testid="email-panel">
            <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-700" /> Email Health Certificate
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Recipients (comma or newline separated) *">
                <textarea rows={2} disabled={emailSending} value={emailForm.recipients}
                  onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                  placeholder="vet@destination.com, broker@example.com"
                  data-testid="email-recipients"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </Field>
              <Field label="CC (optional)">
                <textarea rows={2} disabled={emailSending} value={emailForm.cc}
                  onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                  data-testid="email-cc"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </Field>
              <Field label="Subject (optional override)" full>
                <TextInput disabled={emailSending} value={emailForm.subject}
                  onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                  placeholder={`Health Certificate ${form.certificate_number} — ${form.animal_name || 'Pet'}`}
                  data-testid="email-subject" />
              </Field>
              <Field label="Cover note (optional)" full>
                <textarea rows={3} disabled={emailSending} value={emailForm.message}
                  onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                  placeholder="Hi, please find attached the signed health certificate for our upcoming export…"
                  data-testid="email-message"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </Field>
            </div>
            {emailReview && <div className="mt-4 rounded-lg border border-emerald-200 p-3"><EmailRecipients email={emailReview} testId="health-email-preview-recipients" /></div>}
            <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
              {emailResult && <span role="status" data-testid="health-email-result" className="break-words text-sm text-foreground/70">{emailResult}</span>}
              <button onClick={sendEmail} disabled={emailSending} data-testid="email-send-btn"
                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                {emailSending ? (emailReview ? 'Sending…' : 'Reviewing…') : (emailReview ? 'Confirm and send' : 'Review recipients')}
              </button>
            </div>
          </div>
        )}

        {/* Animal Identification */}
        <Section title="Consignor (Origin)">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name / Details" full><TextInput value={form.consignor_name} onChange={(e) => setField('consignor_name', e.target.value)} data-testid="consignor-name" /></Field>
            <Field label="Address (one line per row)" full><TextArea value={form.consignor_address} onChange={(e) => setField('consignor_address', e.target.value)} data-testid="consignor-address" placeholder="Line 1&#10;Line 2&#10;City, Postcode&#10;Country" /></Field>
            <Field label="Telephone"><TextInput value={form.consignor_telephone} onChange={(e) => setField('consignor_telephone', e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Consignee (Destination)">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Name / Details" full><TextInput value={form.consignee_name} onChange={(e) => setField('consignee_name', e.target.value)} data-testid="consignee-name" /></Field>
            <Field label="Address (one line per row)" full><TextArea value={form.consignee_address} onChange={(e) => setField('consignee_address', e.target.value)} data-testid="consignee-address" placeholder="Line 1&#10;Line 2&#10;City, Postcode&#10;Country" /></Field>
            <Field label="Telephone"><TextInput value={form.consignee_telephone} onChange={(e) => setField('consignee_telephone', e.target.value)} /></Field>
          </div>
        </Section>

        <Section title="Animal Details">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Species"><TextInput value={form.species} onChange={(e) => setField('species', e.target.value)} placeholder="Canine / Feline / Equine" /></Field>
            <Field label="Breed"><TextInput value={form.breed} onChange={(e) => setField('breed', e.target.value)} /></Field>
            <Field label="Name"><TextInput value={form.animal_name} onChange={(e) => setField('animal_name', e.target.value)} data-testid="animal-name" /></Field>
            <Field label="Age / Birthdate"><TextInput value={form.age_or_birthdate} onChange={(e) => setField('age_or_birthdate', e.target.value)} /></Field>
            <Field label="Colour & Markings"><TextInput value={form.colour_markings} onChange={(e) => setField('colour_markings', e.target.value)} /></Field>
            <Field label="Sex & Status"><TextInput value={form.sex_and_status} onChange={(e) => setField('sex_and_status', e.target.value)} placeholder="Male / Neutered" /></Field>
            <Field label="Microchip Number"><TextInput value={form.microchip_number} onChange={(e) => setField('microchip_number', e.target.value)} /></Field>
            <Field label="Microchip Location"><TextInput value={form.microchip_location} onChange={(e) => setField('microchip_location', e.target.value)} /></Field>
            <Field label="Implant Date"><TextInput value={form.microchip_implant_date} onChange={(e) => setField('microchip_implant_date', e.target.value)} placeholder="dd/mm/yy" /></Field>
          </div>
        </Section>

        <Section title="Rabies Vaccine" right={
          <button type="button" onClick={addRabiesEntry} data-testid="add-rabies-row-btn"
            className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary/90 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add row
          </button>
        }>
          {(form.rabies_entries || []).map((entry, idx) => (
            <div key={idx} className="border border-border rounded-lg p-3 mb-3" data-testid={`rabies-entry-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wider">Rabies vaccination #{idx + 1}</span>
                {(form.rabies_entries || []).length > 1 && (
                  <button type="button" onClick={() => removeRabiesEntry(idx)}
                    data-testid={`remove-rabies-row-${idx}`}
                    className="p-1 text-red-500 hover:bg-red-50 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Vaccination Date (dd/mm/yy)"><TextInput value={entry.vaccination_date} onChange={(e) => setRabiesEntry(idx, 'vaccination_date', e.target.value)} /></Field>
                <Field label="Vaccine Make & Serial Number"><TextInput value={entry.vaccine_make_serial} onChange={(e) => setRabiesEntry(idx, 'vaccine_make_serial', e.target.value)} /></Field>
                <Field label="Validity From"><TextInput value={entry.validity_from} onChange={(e) => setRabiesEntry(idx, 'validity_from', e.target.value)} /></Field>
                <Field label="Validity To"><TextInput value={entry.validity_to} onChange={(e) => setRabiesEntry(idx, 'validity_to', e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Serological Testing">
          {(form.rabies_entries || []).map((entry, idx) => (
            <div key={idx} className="border border-border rounded-lg p-3 mb-3" data-testid={`serology-entry-${idx}`}>
              <div className="mb-2">
                <span className="text-xs font-semibold text-foreground/70 uppercase tracking-wider">For rabies vaccination #{idx + 1}</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Date of Sampling (dd/mm/yy)"><TextInput value={entry.serology_sampling_date} onChange={(e) => setRabiesEntry(idx, 'serology_sampling_date', e.target.value)} /></Field>
                <Field label="Official Diagnostic Laboratory"><TextInput value={entry.serology_lab} onChange={(e) => setRabiesEntry(idx, 'serology_lab', e.target.value)} /></Field>
                <Field label="Antibody Titration Result (IU/ml)"><TextInput value={entry.antibody_titration_result} onChange={(e) => setRabiesEntry(idx, 'antibody_titration_result', e.target.value)} /></Field>
                <Field label="Serology Validity From"><TextInput value={entry.serology_validity_from} onChange={(e) => setRabiesEntry(idx, 'serology_validity_from', e.target.value)} /></Field>
                <Field label="Serology Validity To"><TextInput value={entry.serology_validity_to} onChange={(e) => setRabiesEntry(idx, 'serology_validity_to', e.target.value)} /></Field>
              </div>
            </div>
          ))}
        </Section>

        <Section title="Other Vaccination History" right={
          <button onClick={addOtherVac} data-testid="add-vac-btn"
            className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary/90 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add row
          </button>
        }>
          {form.other_vaccines.length === 0 ? (
            <p className="text-sm text-foreground/50 italic">No other vaccinations entered yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-foreground/60">Date</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-foreground/60">Type</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-foreground/60">Make & Serial</th>
                    <th className="px-2 py-1 text-left text-xs font-semibold text-foreground/60">Valid Until</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {form.other_vaccines.map((v, i) => (
                    <tr key={i}>
                      <td className="px-1 py-1"><TextInput value={v.vaccination_date} onChange={(e) => setOtherVac(i, 'vaccination_date', e.target.value)} /></td>
                      <td className="px-1 py-1"><TextInput value={v.vaccination_type} onChange={(e) => setOtherVac(i, 'vaccination_type', e.target.value)} /></td>
                      <td className="px-1 py-1"><TextInput value={v.vaccine_make_serial} onChange={(e) => setOtherVac(i, 'vaccine_make_serial', e.target.value)} /></td>
                      <td className="px-1 py-1"><TextInput value={v.valid_until} onChange={(e) => setOtherVac(i, 'valid_until', e.target.value)} /></td>
                      <td className="px-1 py-1 text-right">
                        <button onClick={() => removeOtherVac(i)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        <Section title="Veterinarian's Declaration">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Acaricidal Preparation (e.g. Bravecto)"><TextInput value={form.acaricidal_preparation} onChange={(e) => setField('acaricidal_preparation', e.target.value)} /></Field>
            <Field label="Acaricidal Treatment Given">
              <YesNo value={form.acaricidal_given} onChange={(v) => setField('acaricidal_given', v)} testid="acaricidal-yn" />
            </Field>
            <Field label="Tapeworm Preparation (e.g. Drontal)"><TextInput value={form.tapeworm_preparation} onChange={(e) => setField('tapeworm_preparation', e.target.value)} /></Field>
            <Field label="Tapeworm Treatment Given">
              <YesNo value={form.tapeworm_given} onChange={(v) => setField('tapeworm_given', v)} testid="tapeworm-yn" />
            </Field>
          </div>
        </Section>

        <Section title="Veterinarian Signature">
          <p className="text-xs text-foreground/60 mb-3">
            Sign once and save it as your default — every new certificate will then be auto-signed. You can still re-draw or upload a different signature on individual certificates.
          </p>
          {form.signed_at && (
            <div
              data-testid="signature-audit-trail"
              className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-900"
            >
              ✓ Digitally signed by <strong>{form.signed_by || form.veterinarian_name}</strong> on{' '}
              {(() => {
                try {
                  const d = new Date(form.signed_at);
                  return d.toLocaleString('en-GB', {
                    timeZone: 'America/Cayman',
                    day: 'numeric', month: 'long', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  }) + ' KYT';
                } catch { return String(form.signed_at); }
              })()}
            </div>
          )}
          <SignaturePad
            value={form.signature_image}
            onChange={(v) => setField('signature_image', v)}
            onSaveDefault={saveDefaultSig}
            onUseDefault={useDefaultSig}
            onClearDefault={clearDefaultSig}
            hasDefault={Boolean(defaultSignature)}
            testid="vet-signature-pad"
          />
        </Section>

        <Section title="Export & Signature">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Export Date"><TextInput type="date" value={form.export_date} onChange={(e) => setField('export_date', e.target.value)} /></Field>
            <Field label="Returning Date">
              <div className="space-y-2">
                <TextInput
                  type="date"
                  value={form.returning_date === 'N/A' ? '' : form.returning_date}
                  onChange={(e) => setField('returning_date', e.target.value)}
                  disabled={form.returning_date === 'N/A'}
                  data-testid="returning-date-input"
                />
                <label className="flex items-center gap-2 text-xs text-foreground/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.returning_date === 'N/A'}
                    onChange={(e) => setField('returning_date', e.target.checked ? 'N/A' : '')}
                    data-testid="returning-date-na-checkbox"
                  />
                  Not returning to Cayman (N/A — e.g. one-way export)
                </label>
              </div>
            </Field>
            <Field label="CI Passport Number"><TextInput value={form.ci_passport_number} onChange={(e) => setField('ci_passport_number', e.target.value)} /></Field>
            <Field label="Certificate Number">
              <TextInput
                value={form.certificate_number}
                onChange={(e) => setField('certificate_number', e.target.value)}
                placeholder="Leave blank to auto-assign HC-YYYY-NNN"
                data-testid="certificate-number-input"
              />
            </Field>
            <Field label="Certificate Issue Date"><TextInput type="date" value={form.certificate_issue_date} onChange={(e) => setField('certificate_issue_date', e.target.value)} /></Field>
            <Field label="Veterinarian Name"><TextInput value={form.veterinarian_name} onChange={(e) => setField('veterinarian_name', e.target.value)} /></Field>
            <Field label="Veterinarian License #"><TextInput value={form.veterinarian_license} onChange={(e) => setField('veterinarian_license', e.target.value)} /></Field>
          </div>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children, right }) => (
  <section className="bg-white border border-border rounded-xl shadow-sm">
    <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-gradient-to-r from-teal-50 to-white">
      <h2 className="text-sm font-bold tracking-wide uppercase text-foreground/80">{title}</h2>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const YesNo = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    {[
      { v: true, label: 'Yes', cls: 'bg-emerald-600 text-white' },
      { v: false, label: 'No', cls: 'bg-red-500 text-white' },
      { v: null, label: '—', cls: 'bg-gray-200 text-foreground/70' },
    ].map((opt) => (
      <button key={opt.label} onClick={() => onChange(opt.v)} type="button"
        className={`px-3 py-1.5 text-xs rounded-lg font-semibold ${value === opt.v ? opt.cls : 'border border-border bg-white text-foreground/70 hover:bg-gray-50'}`}>
        {opt.label}
      </button>
    ))}
  </div>
);

export default HealthCertificate;
